import { useState } from "react";
import { ChefHat } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { useActiveOrders } from "@/hooks/useActiveOrders";
import { sendWaitingToKitchen } from "@/lib/orderApi";

/** The server's running order on this table and how many of its items have not
 *  gone to the kitchen (the board's unsent_count). */
export function useServerWaiting(tableId) {
  const { token, subscribeRealtime } = usePOS();
  const { orders, refresh } = useActiveOrders(token, subscribeRealtime);
  const row = (orders || []).find(
    o => o.table_id === tableId && (o.status === "open" || o.status === "kot_sent")
  ) || null;
  return { row, waiting: Number(row?.unsent_count) || 0, refresh };
}

/**
 * "Send to kitchen" from the server's own count (owner, 2026-10-25: 17 Gardenn's
 * items sat unsent while Vicky's pad showed no Fire KOT button; the pad believed
 * they had been asked for, the server never printed them, and neither side would
 * move). Shown only when this pad offers nothing to fire and the server says
 * items are waiting. Sending is the ordinary KOT: it prints exactly what the
 * server holds unsent, so pressing it twice cannot print anything twice.
 */
export default function SendWaitingButton({ show, ...props }) {
  // The board is read only while this pad has nothing of its own to fire, so
  // the screen does not run a second board poll the rest of the time.
  return show ? <WaitingSend {...props} /> : null;
}

function WaitingSend({ tableId, className = "", testId = "send-waiting-button", onSent }) {
  const { token } = usePOS();
  const { row, waiting, refresh } = useServerWaiting(tableId);
  const [busy, setBusy] = useState(false);
  if (!row || waiting <= 0) return null;

  const send = async () => {
    setBusy(true);
    try {
      await sendWaitingToKitchen(row.id, token);
      toast.success(`${waiting} item(s) sent to the kitchen`);
      onSent?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || err?.message || "Not sent to the kitchen");
    } finally {
      setBusy(false);
      refresh();
    }
  };

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={send}
      disabled={busy}
      className={`h-12 px-4 rounded-xl bg-action text-action-foreground font-bold text-body flex items-center justify-center gap-2 disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none ${className}`}
    >
      <ChefHat className="w-5 h-5" />
      {busy ? "Sending…" : `Send ${waiting} waiting item(s) to kitchen`}
    </button>
  );
}
